import React from 'react';
import {
  Box,
  Text,
  ActionIcon,
  Button,
  Select,
  Loader,
  Flex,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import classes from './AIAssistPanel.module.css';

const TONE_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'Professional', label: 'Professional' },
  { value: 'Formal', label: 'Formal' },
  { value: 'Friendly', label: 'Friendly' },
  { value: 'Concise', label: 'Concise' },
  { value: 'Detailed', label: 'Detailed' },
  { value: 'Apologetic', label: 'Apologetic' },
  { value: 'Assertive', label: 'Assertive' },
];

export interface AIAssistPanelProps {
  aiAssistOpen: boolean;
  aiLoading: boolean;
  aiSuggestions: string[];
  aiRefineMode: boolean;
  aiRefineInput: string;
  aiRefineTone: string;
  onClose: () => void;
  onRefineModeToggle: () => void;
  onRefineInputChange: (value: string) => void;
  onRefineToneChange: (value: string) => void;
  onUseText: (suggestion: string) => void;
  onRefine: () => void;
}

export default function AIAssistPanel(props: AIAssistPanelProps) {
  if (!props.aiAssistOpen) {
    return null;
  }

  return (
    <Box className={classes.root}>
      {/* AI Panel Header */}
      <Box className={classes.header}>
        <Flex align="center" gap="xs">
          {props.aiRefineMode && (
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={props.onRefineModeToggle}
            >
              <FontAwesomeIcon icon={faArrowLeft} fontSize="0.875rem" />
            </ActionIcon>
          )}
          <img src="images/moo.png" alt="Moo" className={classes.mooIcon} />
          <Text fw={700} size="lg">
            {props.aiRefineMode ? 'Refine Suggestions' : 'Moo Suggestions'}
          </Text>
        </Flex>
        <ActionIcon variant="subtle" size="sm" onClick={props.onClose}>
          <FontAwesomeIcon icon={faXmark} />
        </ActionIcon>
      </Box>

      {/* AI Panel Content */}
      <Box className={classes.content}>
        {props.aiLoading && (
          <Box className={classes.loadingContainer}>
            <Loader size={32} />
            <Text size="md" c="dimmed">
              Generating Suggestions...
            </Text>
          </Box>
        )}
        {!props.aiLoading && props.aiRefineMode && (
          <>
            <Box mb="sm">
              <Text fw={500} mb="xs">
                Describe what you want to refine
              </Text>
              <textarea
                value={props.aiRefineInput}
                onChange={(event) =>
                  props.onRefineInputChange(event.target.value)
                }
                rows={4}
                className={classes.refineTextarea}
                placeholder="Describe your desired changes..."
              />
            </Box>
            <Box mb="sm">
              <Text fw={500} mb="xs">
                Tone
              </Text>
              <Select
                value={props.aiRefineTone}
                onChange={(value) => props.onRefineToneChange(value || 'None')}
                data={TONE_OPTIONS}
                size="sm"
                comboboxProps={{ zIndex: 10000 }}
              />
            </Box>
          </>
        )}
        {!props.aiLoading && !props.aiRefineMode && (
          <>
            {props.aiSuggestions.length > 0 ? (
              props.aiSuggestions.map((suggestion, index) => (
                <Box
                  key={index}
                  className={classes.suggestionCard}
                  onClick={() => props.onUseText(suggestion)}
                >
                  <Text size="sm" className={classes.preWrapText}>
                    {suggestion}
                  </Text>
                </Box>
              ))
            ) : (
              <Box className={classes.emptyCard}>
                <Text size="sm" c="dimmed">
                  No suggestions available. Try refining your request.
                </Text>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* AI Panel Footer */}
      <Box className={classes.footer}>
        {!props.aiRefineMode && (
          <Button
            variant="outline"
            size="sm"
            onClick={props.onRefineModeToggle}
            radius="xl"
            px="md"
            fw={500}
            color="var(--cmail-cowbell-blue)"
          >
            Refine Suggestions
          </Button>
        )}
        {props.aiRefineMode && (
          <Button
            variant="filled"
            onClick={props.onRefine}
            radius="xl"
            px="lg"
            fw={500}
            color="var(--cmail-cowbell-blue)"
          >
            Refine
          </Button>
        )}
      </Box>
    </Box>
  );
}
